import { LightingSettings } from '../hooks/useLightingSettings';
import { CATALOG_TURNTABLE, HERO_STUDIO_SPIN } from './catalogTurntable';

export type LightingConfig = LightingSettings & {
  rimLightPosition?: [number, number, number];
  rimLightColor?: string;
};

export type PublicRenderMode = 'hero' | 'product';
export type CameraTargetMode = 'bounds' | 'fixed';
export type FloorPlaneMode = 'model-base' | 'fixed';
export type FloorShape = 'rectangle' | 'disc';

export type PublicRenderPipeline = {
  id: string;
  mode: PublicRenderMode;
  lighting: LightingConfig;
  backgroundColor: string;
  showEnvironmentBackground: boolean;
  environmentBackgroundBlur: number;
  modelScale: number;
  modelPosition: [number, number, number];
  cameraTargetMode: CameraTargetMode;
  fixedCameraTarget: [number, number, number];
  cameraTargetOffset: [number, number, number];
  cameraViewAzimuth: number;
  cameraViewElevation: number;
  cameraViewDistance: number;
  environmentRotation: number;
  modelRotation: [number, number, number];
  autoRotateModel: boolean;
  interactionMode: 'turntable';
  toneExposure: number;
  reflectionEnabled: boolean;
  floorShape: FloorShape;
  floorReflectivity: number;
  floorEdgeSoftness: number;
  reflectionOpacity: number;
  reflectionVisibility: number;
  floorPlaneMode: FloorPlaneMode;
  reflectionPlaneYOffset: number;
  floorSurfacePlaneYOffset?: number;
  reflectorSurfaceOpacity: number;
  reflectionRadiusMultiplier: number;
  reflectionForwardOffsetMultiplier: number;
  reflectionWidthMultiplier: number;
  reflectionDepthMultiplier: number;
  reflectionForegroundFade: number;
  reflectionDepthFade: number;
  reflectionNearBaseSharpness: number;
  reflectionViewerSideSoftness: number;
  reflectionResolution?: number;
  reflectionCloneEnabled: boolean;
  floorSurfaceOpacity: number;
  floorColor: string;
  showContactShadow: boolean;
  shadowOpacity: number;
  shadowRadiusMultiplier: number;
  shadowPlaneEpsilon: number;
  dragYawSensitivity: number;
  autoRotateSpeed: number;
  autoRotateDirection: 1 | -1;
  tiltMaxDeg: number;
  tiltSensitivity: number;
  tiltResetOnRelease: boolean;
};

export type PublicRenderPipelineOverrides = Partial<Pick<
  PublicRenderPipeline,
  | 'backgroundColor'
  | 'showEnvironmentBackground'
  | 'environmentBackgroundBlur'
  | 'lighting'
  | 'toneExposure'
  | 'showContactShadow'
  | 'shadowOpacity'
  | 'shadowRadiusMultiplier'
  | 'shadowPlaneEpsilon'
  | 'reflectionEnabled'
  | 'floorShape'
  | 'floorReflectivity'
  | 'floorEdgeSoftness'
  | 'reflectionOpacity'
  | 'reflectionVisibility'
  | 'floorPlaneMode'
  | 'reflectionPlaneYOffset'
  | 'floorSurfacePlaneYOffset'
  | 'reflectorSurfaceOpacity'
  | 'reflectionRadiusMultiplier'
  | 'reflectionForwardOffsetMultiplier'
  | 'reflectionWidthMultiplier'
  | 'reflectionDepthMultiplier'
  | 'reflectionForegroundFade'
  | 'reflectionDepthFade'
  | 'reflectionNearBaseSharpness'
  | 'reflectionViewerSideSoftness'
  | 'reflectionResolution'
  | 'floorSurfaceOpacity'
  | 'floorColor'
  | 'modelPosition'
  | 'cameraTargetMode'
  | 'fixedCameraTarget'
  | 'cameraTargetOffset'
  | 'cameraViewAzimuth'
  | 'cameraViewElevation'
  | 'cameraViewDistance'
  | 'environmentRotation'
  | 'modelScale'
  | 'reflectionCloneEnabled'
  | 'autoRotateModel'
  | 'dragYawSensitivity'
  | 'autoRotateSpeed'
  | 'autoRotateDirection'
  | 'tiltMaxDeg'
  | 'tiltSensitivity'
  | 'tiltResetOnRelease'
>> & {
  /** Section backdrop: slide floor along depth without moving the vase. */
  floorPlaneForwardOffset?: number;
  /** Section backdrop: nudge reflection edge up/down on screen (alpha fade). */
  reflectionHorizonScreenShift?: number;
};

export const HERO_LIGHTING_PRESET: LightingConfig = {
  environmentPreset: 'studio',
  environmentIntensity: 0.85,
  ambientIntensity: 0.15,
  hemisphereIntensity: 0.15,
  hemisphereSkyColor: '#f7f5f1',
  hemisphereGroundColor: '#1a1a1a',
  keyLightEnabled: true,
  keyLightIntensity: 0.45,
  keyLightPosition: [3, 4, 6],
  keyLightColor: '#ffffff',
  keyLightAzimuth: 63.43,
  keyLightElevation: 30.81,
  fillLightEnabled: true,
  fillLightIntensity: 0.2,
  fillLightPosition: [-3, 2.5, 6],
  fillLightColor: '#fff7ef',
  rimLightEnabled: true,
  rimLightIntensity: 0.35,
  rimLightPosition: [0, 3.5, -24],
  rimLightColor: '#ffffff',
  lightSoftness: 1.0,
  keySoftboxScale: 1.0,
  keySoftboxDistanceScale: 1.0,
  keySoftboxIntensityScale: 1.0,
};

export const PRODUCT_LIGHTING_PRESET: LightingConfig = {
  environmentPreset: 'none',
  environmentIntensity: 0,
  ambientIntensity: 0.45,
  hemisphereIntensity: 0.45,
  hemisphereSkyColor: '#f7f5f1',
  hemisphereGroundColor: '#1e1e1e',
  keyLightEnabled: true,
  keyLightIntensity: 0.3,
  keyLightPosition: [2.5, 3.5, 6],
  keyLightColor: '#ffffff',
  keyLightAzimuth: 58,
  keyLightElevation: 34,
  fillLightEnabled: true,
  fillLightIntensity: 0.32,
  fillLightPosition: [-2.5, 2.5, 6],
  fillLightColor: '#fff7ef',
  rimLightEnabled: true,
  rimLightIntensity: 0.12,
  rimLightPosition: [0, 3, -22],
  rimLightColor: '#ffffff',
  lightSoftness: 2.4,
  keySoftboxScale: 2.2,
  keySoftboxDistanceScale: 1.3,
  keySoftboxIntensityScale: 0.8,
};

/** Public site renderer presets — Hero (Enter) vs Product studio (catalog/customiser). */
export const modeLabel = (mode: PublicRenderMode) =>
  mode === 'hero' ? 'Hero (Enter Screen)' : 'Product studio (Main Site)';

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

export const defaultReflectionVisibility = (mode: PublicRenderMode, floorPresence: number) => {
  if (mode === 'hero') {
    const presenceNorm = clamp01(floorPresence / 0.12);
    return clamp01(0.2 + presenceNorm * 0.65);
  }

  return 0.9;
};

export const defaultPipelineForMode = (mode: PublicRenderMode): PublicRenderPipeline => ({
  id: mode,
  mode,
  lighting: mode === 'hero' ? HERO_LIGHTING_PRESET : PRODUCT_LIGHTING_PRESET,
  backgroundColor: mode === 'hero' ? '#000000' : '#fefefe',
  showEnvironmentBackground: false,
  environmentBackgroundBlur: 0,
  modelScale: 0.3,
  modelPosition: [0, -2, 0],
  cameraTargetMode: 'bounds',
  fixedCameraTarget: [0, 0, 0],
  cameraTargetOffset: [0, 0, 0],
  cameraViewAzimuth: mode === 'hero' ? -7 : 0,
  cameraViewElevation: mode === 'hero' ? -2 : 0,
  cameraViewDistance: mode === 'hero' ? 22 : 20,
  environmentRotation: mode === 'hero' ? 28 : 0,
  modelRotation: [0, 0, 0],
  autoRotateModel: true,
  interactionMode: 'turntable',
  toneExposure: mode === 'hero' ? 0.82 : 1.0,
  reflectionEnabled: true,
  floorShape: 'rectangle',
  floorReflectivity: mode === 'hero' ? 0.52 : 0.35,
  floorEdgeSoftness: mode === 'hero' ? 0.42 : 0.2,
  reflectionOpacity: mode === 'hero' ? 0.22 : 0.03,
  reflectionVisibility: mode === 'hero' ? 0.88 : defaultReflectionVisibility(mode, 0.03),
  floorPlaneMode: mode === 'hero' ? 'fixed' : 'model-base',
  reflectionPlaneYOffset: mode === 'hero' ? -0.16 : 0,
  reflectorSurfaceOpacity: mode === 'hero' ? 0.1 : 1,
  reflectionRadiusMultiplier: mode === 'hero' ? 1.62 : 2.5,
  reflectionForwardOffsetMultiplier: mode === 'hero' ? 0.22 : 0,
  reflectionWidthMultiplier: mode === 'hero' ? 1.08 : 1,
  reflectionDepthMultiplier: mode === 'hero' ? 1.38 : 1,
  reflectionForegroundFade: mode === 'hero' ? 0.86 : 0.22,
  reflectionDepthFade: mode === 'hero' ? 0.66 : 0.28,
  reflectionNearBaseSharpness: mode === 'hero' ? 0.52 : 0.35,
  reflectionViewerSideSoftness: mode === 'hero' ? 0.5 : 0.28,
  reflectionResolution: mode === 'hero' ? 1536 : undefined,
  reflectionCloneEnabled: false,
  floorSurfaceOpacity: mode === 'hero' ? 0.14 : 1,
  floorColor: mode === 'hero' ? '#121010' : '#fefefe',
  showContactShadow: true,
  shadowOpacity: mode === 'hero' ? 0.3 : 0.18,
  shadowRadiusMultiplier: mode === 'hero' ? 2.6 : 3,
  shadowPlaneEpsilon: 0.002,
  dragYawSensitivity: mode === 'hero' ? HERO_STUDIO_SPIN.dragYawSensitivity : CATALOG_TURNTABLE.dragYawSensitivity,
  /** Slow catalog spin — resumes after drag release. */
  autoRotateSpeed: mode === 'hero' ? HERO_STUDIO_SPIN.autoRotateSpeed : CATALOG_TURNTABLE.autoRotateSpeed,
  autoRotateDirection: CATALOG_TURNTABLE.autoRotateDirection,
  tiltMaxDeg: CATALOG_TURNTABLE.tiltMaxDeg,
  tiltSensitivity: CATALOG_TURNTABLE.tiltSensitivity,
  tiltResetOnRelease: CATALOG_TURNTABLE.tiltResetOnRelease,
});

export const DEFAULT_PUBLIC_RENDER_PIPELINES: Record<PublicRenderMode, PublicRenderPipeline> = {
  hero: defaultPipelineForMode('hero'),
  product: defaultPipelineForMode('product'),
};
